import axios from 'axios';
import { getAuthHeaders, getRequestConfig } from '../utils/authUtils';

// Get base API URL from environment variable
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

/**
 * Service for managing subtitle sections
 */
class SectionManagementService {
  /**
   * Get all sections for a video language
   * 
   * @param {string} videoId - The video ID
   * @param {string} languageName - The language name
   * @returns {Promise} - Promise resolving to API response with sections
   */
  async getSections(videoId, languageName) {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/v3/videos/${videoId}/languages/${languageName}/sections`,
        getRequestConfig({}, true)
      );
      return response;
    } catch (error) {
      console.error('Error fetching sections:', error);
      throw error;
    }
  }

  /**
   * Get a specific section by ID
   * 
   * @param {string} videoId - The video ID
   * @param {string} languageName - The language name
   * @param {string} sectionId - The section ID
   * @returns {Promise} - Promise resolving to API response with section details
   */
  async getSection(videoId, languageName, sectionId) {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/v3/videos/${videoId}/languages/${languageName}/sections/${sectionId}`,
        { headers: getAuthHeaders() }
      );
      return response;
    } catch (error) {
      console.error('Error fetching section details:', error);
      throw error;
    }
  }


  /**
   * Start editing a specific section
   * 
   * @param {string} videoId - The video ID
   * @param {string} languageName - The language name
   * @param {string} sectionId - The section ID
   * @returns {Promise} - Promise resolving to API response with section details
   */
  async startSectionEditing(videoId, languageName, sectionId) {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/v3/videos/${videoId}/languages/${languageName}/sections/${sectionId}/edit/start`,
        {},
        { headers: getAuthHeaders() }
      );
      return response;
    } catch (error) {
      console.error('Error starting section editing:', error);
      throw error;
    }
  }

  /**
   * Save changes to a section
   * 
   * @param {string} videoId - The video ID
   * @param {string} languageName - The language name
   * @param {string} sectionId - The section ID
   * @param {string} vttContent - The VTT content
   * @returns {Promise} - Promise resolving to API response with updated section
   */
  async saveSection(videoId, languageName, sectionId, vttContent) {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/v3/videos/${videoId}/languages/${languageName}/sections/${sectionId}/edit/save`,
        { vttContent },
        { headers: getAuthHeaders() }
      );
      return response;
    } catch (error) {
      console.error('Error saving section:', error);
      throw error;
    }
  }

  /**
   * End section editing session
   * 
   * @param {string} videoId - The video ID
   * @param {string} languageName - The language name
   * @param {string} sectionId - The section ID
   * @returns {Promise} - Promise resolving to API response
   */
  async endSectionEditing(videoId, languageName, sectionId) {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/v3/videos/${videoId}/languages/${languageName}/sections/${sectionId}/edit/end`,
        {},
        { headers: getAuthHeaders() }
      );
      return response;
    } catch (error) {
      console.error('Error ending section editing:', error);
      throw error;
    }
  }

  /**
   * End section editing using sendBeacon (for page unload)
   * 
   * @param {string} videoId - The video ID
   * @param {string} languageName - The language name
   * @param {string} sectionId - The section ID
   * @returns {boolean} - Whether the beacon was successfully queued
   */
  endSectionEditingBeacon(videoId, languageName, sectionId) {
    try {
      const headers = getAuthHeaders();
      const formData = new FormData();
      
      // Add auth headers to form data
      Object.keys(headers).forEach(key => {
        formData.append(key, headers[key]);
      });

      return navigator.sendBeacon(
        `${API_BASE_URL}/api/v3/videos/${videoId}/languages/${languageName}/sections/${sectionId}/edit/end-beacon`,
        formData
      );
    } catch (error) {
      console.error('Error sending end editing beacon:', error);
      return false;
    }
  }

  /**
   * Merge all sections for a language
   * 
   * @param {string} videoId - The video ID
   * @param {string} languageName - The language name
   * @returns {Promise} - Promise resolving to API response
   */
  async mergeSections(videoId, languageName) {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/v3/videos/${videoId}/languages/${languageName}/sections/merge`,
        {},
        { headers: getAuthHeaders() }
      );
      return response;
    } catch (error) {
      console.error('Error merging sections:', error);
      throw error;
    }
  }

  /**
   * Delete a section
   * 
   * @param {string} videoId - The video ID
   * @param {string} languageName - The language name
   * @param {string} sectionId - The section ID
   * @returns {Promise} - Promise resolving to API response
   */
  async deleteSection(videoId, languageName, sectionId) {
    try {
      const response = await axios.delete(
        `${API_BASE_URL}/api/v3/videos/${videoId}/languages/${languageName}/sections/${sectionId}`,
        getRequestConfig({}, true)
      );
      return response;
    } catch (error) {
      console.error('Error deleting section:', error);
      throw error;
    }
  }

  /**
   * Assign a section to a user
   * 
   * @param {string} videoId - The ID of the video
   * @param {string} languageName - The language name
   * @param {string} sectionId - The section ID
   * @param {string} assigneeEmail - Email of the user to assign
   * @returns {Promise} - Promise resolving to API response
   */
  async assignSection(videoId, languageName, sectionId, assigneeEmail) {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/v3/videos/${videoId}/languages/${languageName}/sections/${sectionId}/assign`,
        { assignee: assigneeEmail },
        { headers: getAuthHeaders() }
      );
      return response;
    } catch (error) {
      console.error('Error assigning section:', error);
      throw error;
    }
  }

  /**
   * Create sections for a video subtitle
   * 
   * @param {string} videoId - The ID of the video
   * @param {string} languageName - The language name
   * @param {number} numberOfSections - Number of sections to create
   * @returns {Promise} - Promise resolving to API response
   */
  async createSections(videoId, languageName, numberOfSections) {
    try {
      // Note: You have two methods with the same name but different endpoints.
      // I've kept both, but you should probably consolidate these.
      const response = await axios.post(
        `${API_BASE_URL}/api/v3/videos/${videoId}/languages/${languageName}/sections`,
        { numberOfSections },
        { headers: getAuthHeaders() }
      );
      return response;
    } catch (error) {
      console.error('Error creating sections:', error);
      throw error;
    }
  }
}

// Create a singleton instance
const sectionManagementService = new SectionManagementService();
export default sectionManagementService;